import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import BlindClosingModal from './BlindClosingModal';
import api from '../services/api';

/**
 * CashBoxOverview - Shows current session status and balance
 * Displays balance cards and action buttons for expenses and closing
 * When session is closed, shows a full closing report with arqueo, metrics, etc.
 */
const CashBoxOverview = ({ session, balance, onRefresh, onSessionClosed, onOpenExpense }) => {
    const [showClosingModal, setShowClosingModal] = useState(false);
    const [notes, setNotes] = useState('');
    const [notesSaved, setNotesSaved] = useState(false);
    const [sessionDetails, setSessionDetails] = useState(null);
    const saveTimer = useRef(null);

    // Load existing notes on mount / session change
    useEffect(() => {
        if (session?.notes) {
            // Strip auto-close artifacts from notes display
            setNotes(session.notes.replace(/\s*\[Auto-closed duplicate\]/g, '').trim());
        } else {
            setNotes('');
        }
    }, [session?.id]);

    // Fetch session details for closed sessions (to get movements count for metrics)
    useEffect(() => {
        if (session?.id && session.status === 'closed') {
            api.get(`/cash-box/sessions/${session.id}`)
                .then(res => setSessionDetails(res.data))
                .catch(err => console.error('Error fetching session details:', err));
        } else {
            setSessionDetails(null);
        }
    }, [session?.id, session?.status]);

    const handleNotesChange = (value) => {
        setNotes(value);
        setNotesSaved(false);
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(async () => {
            try {
                await api.patch(`/cash-box/sessions/${session.id}/notes`, { notes: value });
                setNotesSaved(true);
                setTimeout(() => setNotesSaved(false), 2000);
            } catch (err) {
                console.error('Error saving notes:', err);
            }
        }, 1500);
    };

    const formatCurrency = (amount) => {
        return `$${Math.round(parseFloat(amount || 0)).toLocaleString('es-CO')}`;
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleString('es-CO');
    };

    if (!session || !balance) {
        return <div className="text-center text-gray-600">Cargando información de caja...</div>;
    };

    const sessionDuration = () => {
        const opened = new Date(session.opened_at);
        const closed = session.closed_at ? new Date(session.closed_at) : new Date();
        const now = new Date();
        const diff = session.closed_at ? (closed - opened) : (now - opened);
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        return `${hours}h ${minutes}m`;
    };

    // ---- Closing Report Helpers ----
    const isClosed = session.status === 'closed';

    const getDifference = () => parseFloat(session.difference || 0);

    const getDifferenceStatus = () => {
        const diff = getDifference();
        if (Math.abs(diff) <= 100) return 'exact';
        if (diff > 0) return 'warning'; // sobrante
        return 'danger'; // faltante
    };

    const getDifferenceLabel = () => {
        const diff = getDifference();
        if (Math.abs(diff) <= 100) return '✅ Cuadrado';
        if (diff > 0) return '⚠️ Sobrante';
        return '🔴 Faltante';
    };

    // Parse closing breakdown for arqueo table
    const getBreakdown = () => {
        let breakdown = session.closing_breakdown;
        if (!breakdown) return null;
        if (typeof breakdown === 'string') {
            try { breakdown = JSON.parse(breakdown); } catch { return null; }
        }
        return breakdown;
    };

    const BILL_DENOMINATIONS = [
        { key: '100k', label: '$100.000', value: 100000 },
        { key: '50k', label: '$50.000', value: 50000 },
        { key: '20k', label: '$20.000', value: 20000 },
        { key: '10k', label: '$10.000', value: 10000 },
        { key: '5k', label: '$5.000', value: 5000 },
        { key: '2k', label: '$2.000', value: 2000 },
        { key: '1k', label: '$1.000', value: 1000 },
        { key: 'coins', label: 'Monedas', value: 1, isCoins: true },
    ];

    // Metrics from session details
    const getMetrics = () => {
        if (!sessionDetails?.movements) return { incomeCount: 0, expenseCount: 0, avgIncome: 0 };
        const movements = sessionDetails.movements;
        const incomeOps = movements.filter(m => m.type === 'sale_income' || m.type === 'extra_income');
        const expenseOps = movements.filter(m => m.type !== 'sale_income' && m.type !== 'extra_income');
        const totalIncome = parseFloat(balance.total_income || 0);
        return {
            incomeCount: incomeOps.length,
            expenseCount: expenseOps.length,
            avgIncome: incomeOps.length > 0 ? Math.round(totalIncome / incomeOps.length) : 0
        };
    };

    const exportUrl = (format) => {
        const baseUrl = process.env.REACT_APP_API_URL || '';
        const token = localStorage.getItem('token');
        return `${baseUrl}/api/cash-box/sessions/${session.id}/export-${format}?token=${token}`;
    };

    return (
        <div className="space-y-6">
            {/* Balance Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="balance-card bg-blue-50 border-blue-200">
                    <CardContent className="pt-6">
                        <div className="text-sm text-blue-600 font-medium">Base Inicial</div>
                        <div className="text-2xl font-bold text-blue-700 mt-2">
                            {formatCurrency(balance.opening_balance)}
                        </div>
                    </CardContent>
                </Card>

                <Card className="balance-card bg-green-50 border-green-200">
                    <CardContent className="pt-6">
                        <div className="text-sm text-green-600 font-medium">Ingresos Sesión</div>
                        <div className="text-2xl font-bold text-green-700 mt-2">
                            {formatCurrency(balance.total_income)}
                        </div>
                        {(balance.income_bodega != null || balance.income_ruta != null) && (
                            <div className="mt-2 pt-2 border-t border-green-200">
                                <div className="flex gap-4">
                                    <div className="flex-1">
                                        <div className="text-[10px] text-green-500 font-bold uppercase">🏪 Bodega</div>
                                        <div className="text-sm font-bold text-green-700">{formatCurrency(balance.income_bodega)}</div>
                                        {(balance.income_bodega_siigo > 0 || balance.income_bodega_int > 0) && (
                                            <div className="mt-1 space-y-0.5">
                                                {balance.income_bodega_siigo > 0 && (
                                                    <div className="text-[9px] text-gray-500">Siigo: {formatCurrency(balance.income_bodega_siigo)}</div>
                                                )}
                                                {balance.income_bodega_int > 0 && (
                                                    <div className="text-[9px] text-orange-500">INT: {formatCurrency(balance.income_bodega_int)}</div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex-1">
                                        <div className="text-[10px] text-green-500 font-bold uppercase">🚚 Ruta</div>
                                        <div className="text-sm font-bold text-green-700">{formatCurrency(balance.income_ruta)}</div>
                                        {(balance.income_ruta_siigo > 0 || balance.income_ruta_int > 0 || balance.route_expenses_total > 0) && (
                                            <div className="mt-1 space-y-0.5">
                                                {balance.income_ruta_siigo > 0 && (
                                                    <div className="text-[9px] text-gray-500">Siigo: {formatCurrency(balance.income_ruta_siigo)}</div>
                                                )}
                                                {balance.income_ruta_int > 0 && (
                                                    <div className="text-[9px] text-orange-500">INT: {formatCurrency(balance.income_ruta_int)}</div>
                                                )}
                                                {balance.route_expenses_total > 0 && (
                                                    <div className="text-[9px] text-red-500">Gastos: -{formatCurrency(balance.route_expenses_total)}</div>
                                                )}
                                            </div>
                                        )}
                                        {/* Per-route breakdown */}
                                        {balance.ruta_breakdown && balance.ruta_breakdown.length > 1 && (
                                            <div className="mt-1.5 pt-1.5 border-t border-green-200 space-y-1">
                                                {balance.ruta_breakdown.map((rb, i) => (
                                                    <div key={i} className="flex items-center justify-between text-[9px]">
                                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 font-bold">
                                                            {rb.route_number ? `R${rb.route_number}` : 'S/R'}
                                                        </span>
                                                        <span className="text-gray-600 font-medium">{formatCurrency(rb.total)}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    {parseFloat(balance.income_portfolio || 0) > 0 && (
                                        <div className="flex-1">
                                            <div className="text-[10px] text-emerald-600 font-bold uppercase">💼 Cartera</div>
                                            <div className="text-sm font-bold text-emerald-700">{formatCurrency(balance.income_portfolio)}</div>
                                            <div className="text-[9px] text-gray-400 italic">Abonos hoy</div>
                                        </div>
                                    )}
                                    {parseFloat(balance.income_extra || 0) > 0 && (
                                        <div>
                                            <div className="text-[10px] text-green-500 font-bold uppercase">➕ Otros</div>
                                            <div className="text-sm font-bold text-green-700">{formatCurrency(balance.income_extra)}</div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>

                <Card className="balance-card bg-red-50 border-red-200">
                    <CardContent className="pt-6">
                        <div className="text-sm text-red-600 font-medium">Egresos Hoy</div>
                        <div className="text-2xl font-bold text-red-700 mt-2">
                            -{formatCurrency(balance.total_expenses)}
                        </div>
                </CardContent>
                </Card>

                <Card className="balance-card bg-purple-50 border-purple-200">
                    <CardContent className="pt-6">
                        <div className="text-sm text-purple-600 font-medium">Saldo Esperado</div>
                        <div className="text-2xl font-bold text-purple-700 mt-2">
                            {formatCurrency(balance.expected_balance)}
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Electronic Payments Card */}
            {balance.electronic_total > 0 && (
                <Card className="balance-card bg-teal-50 border-teal-200">
                    <CardContent className="pt-6">
                        <div className="text-sm text-teal-600 font-medium">💳 Pagos Electrónicos</div>
                        <div className="text-2xl font-bold text-teal-700 mt-2">
                            {formatCurrency(balance.electronic_total)}
                        </div>
                        <div className="text-xs text-teal-500 mt-1">
                            {balance.electronic_count || 0} operaciones (no afectan saldo en efectivo)
                        </div>
                        {balance.electronic_by_method && balance.electronic_by_method.length > 0 && (
                            <div className="mt-2 pt-2 border-t border-teal-200 space-y-1">
                                {balance.electronic_by_method.map((m, i) => (
                                    <div key={i} className="flex justify-between text-xs">
                                        <span className="text-teal-600 capitalize">{m.payment_method.replace(/_/g, ' ')}</span>
                                        <span className="font-bold text-teal-700">{formatCurrency(m.total)} ({m.count})</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Session Info */}
            <Card>
                <CardHeader>
                    <CardTitle>📋 Información de la Sesión</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                            <div className="text-sm text-gray-600">Caja:</div>
                            <div className="font-semibold">{session.box_name}</div>
                        </div>
                        <div>
                            <div className="text-sm text-gray-600">Abierta por:</div>
                            <div className="font-semibold">{session.opened_by_name || 'Usuario'}</div>
                        </div>
                        <div>
                            <div className="text-sm text-gray-600">Inicio:</div>
                            <div className="font-semibold">{formatDateTime(session.opened_at)}</div>
                        </div>
                        <div>
                            <div className="text-sm text-gray-600">Duración / Estado:</div>
                            <div className="font-semibold">{sessionDuration()} ({session.status === 'open' ? '🟢 Abierta' : '🔴 Cerrada'})</div>
                        </div>

                        {session.status === 'closed' && (
                            <>
                                <div>
                                    <div className="text-sm text-gray-600">Cerrada por:</div>
                                    <div className="font-semibold">{session.closed_by_name || '-'}</div>
                                </div>
                                <div>
                                    <div className="text-sm text-gray-600">Cierre:</div>
                                    <div className="font-semibold">{formatDateTime(session.closed_at)}</div>
                                </div>
                                <div>
                                    <div className="text-sm text-gray-600">Diferencia:</div>
                                    <div className={`font-semibold ${parseFloat(session.difference || 0) < 0 ? 'text-red-600' : 'text-green-600'}`}>
                                        {formatCurrency(session.difference)}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Observations */}
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="flex items-center justify-between">
                        <span>📝 Observaciones del Día</span>
                        {notesSaved && (
                            <span className="text-xs font-normal text-green-600 bg-green-50 px-2 py-1 rounded-full">✓ Guardado</span>
                        )}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <textarea
                        value={notes}
                        onChange={(e) => handleNotesChange(e.target.value)}
                        placeholder="Escribe novedades, observaciones o comentarios del día..."
                        rows={3}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
                        readOnly={session.status !== 'open'}
                    />
                </CardContent>
            </Card>

            {/* ============= CLOSING REPORT (Closed sessions only) ============= */}
            {isClosed && (
                <div className="closing-report">
                    {/* Report Header */}
                    <div className="closing-report-header">
                        <h2>📋 Informe de Cierre de Caja</h2>
                        <span className={`status-badge ${getDifferenceStatus()}`}>
                            {getDifferenceLabel()}
                        </span>
                    </div>

                    {/* Cross-Reference with Previous Session */}
                    {sessionDetails?.previous_session && (() => {
                        const prev = sessionDetails.previous_session;
                        const prevDeclared = parseFloat(prev.closing_balance_declared || 0);
                        const currentBase = parseFloat(balance.opening_balance || 0);
                        const carryDiff = currentBase - prevDeclared;
                        const isCarryExact = Math.abs(carryDiff) <= 100;
                        return (
                            <div className={`crossref-section ${isCarryExact ? 'crossref-ok' : 'crossref-alert'}`}>
                                <div className="crossref-header">
                                    <span className="crossref-icon">{isCarryExact ? '🔗' : '⚠️'}</span>
                                    <span className="crossref-title">Cruce con Sesión Anterior (#{prev.id})</span>
                                    <span className={`crossref-badge ${isCarryExact ? 'badge-ok' : 'badge-alert'}`}>
                                        {isCarryExact ? '✅ Coincide' : '⚠️ Discrepancia'}
                                    </span>
                                </div>
                                <div className="crossref-grid">
                                    <div className="crossref-item">
                                        <div className="crossref-label">Cierre Anterior (Declarado)</div>
                                        <div className="crossref-value">{formatCurrency(prevDeclared)}</div>
                                        <div className="crossref-sub">
                                            {prev.closed_by_name ? `Cerrado por ${prev.closed_by_name}` : ''} · {formatDateTime(prev.closed_at)}
                                        </div>
                                    </div>
                                    <div className="crossref-arrow">{isCarryExact ? '→' : '⚡'}</div>
                                    <div className="crossref-item">
                                        <div className="crossref-label">Apertura Actual (Base)</div>
                                        <div className="crossref-value">{formatCurrency(currentBase)}</div>
                                        <div className="crossref-sub">
                                            {session.opened_by_name ? `Abierto por ${session.opened_by_name}` : ''} · {formatDateTime(session.opened_at)}
                                        </div>
                                    </div>
                                    {!isCarryExact && (
                                        <div className="crossref-diff">
                                            <div className="crossref-label">Diferencia de Arrastre</div>
                                            <div className={`crossref-value ${carryDiff > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                {carryDiff > 0 ? '+' : ''}{formatCurrency(carryDiff)}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })()}

                    {/* Financial Summary - 6 cards */}
                    <div className="closing-financial-grid">
                        <div className="closing-card" style={{ background: '#eff6ff', borderColor: '#bfdbfe' }}>
                            <div className="label" style={{ color: '#2563eb' }}>Base Inicial</div>
                            <div className="amount" style={{ color: '#1d4ed8' }}>
                                {formatCurrency(balance.opening_balance)}
                            </div>
                        </div>

                        <div className="closing-card" style={{ background: '#f0fdf4', borderColor: '#bbf7d0' }}>
                            <div className="label" style={{ color: '#16a34a' }}>Total Ingresos</div>
                            <div className="amount" style={{ color: '#15803d' }}>
                                {formatCurrency(balance.total_income)}
                            </div>
                            {(balance.income_bodega != null || balance.income_ruta != null) && (
                                <div className="sub-detail" style={{ color: '#166534' }}>
                                    🏪 {formatCurrency(balance.income_bodega || 0)} · 🚚 {formatCurrency(balance.income_ruta || 0)}
                                </div>
                            )}
                        </div>

                        <div className="closing-card" style={{ background: '#fef2f2', borderColor: '#fecaca' }}>
                            <div className="label" style={{ color: '#dc2626' }}>Total Egresos</div>
                            <div className="amount" style={{ color: '#b91c1c' }}>
                                -{formatCurrency(balance.total_expenses)}
                            </div>
                        </div>

                        <div className="closing-card" style={{ background: '#faf5ff', borderColor: '#e9d5ff' }}>
                            <div className="label" style={{ color: '#9333ea' }}>Saldo Esperado</div>
                            <div className="amount" style={{ color: '#7e22ce' }}>
                                {formatCurrency(session.closing_balance_expected || balance.expected_balance)}
                            </div>
                        </div>

                        <div className="closing-card" style={{ background: '#eef2ff', borderColor: '#c7d2fe' }}>
                            <div className="label" style={{ color: '#4f46e5' }}>Saldo Declarado</div>
                            <div className="amount" style={{ color: '#4338ca' }}>
                                {formatCurrency(session.closing_balance_declared)}
                            </div>
                        </div>

                        <div className="closing-card" style={{
                            background: getDifferenceStatus() === 'exact' ? '#f0fdf4' : getDifferenceStatus() === 'warning' ? '#fffbeb' : '#fef2f2',
                            borderColor: getDifferenceStatus() === 'exact' ? '#bbf7d0' : getDifferenceStatus() === 'warning' ? '#fde68a' : '#fecaca'
                        }}>
                            <div className="label" style={{
                                color: getDifferenceStatus() === 'exact' ? '#16a34a' : getDifferenceStatus() === 'warning' ? '#d97706' : '#dc2626'
                            }}>Diferencia</div>
                            <div className="amount" style={{
                                color: getDifferenceStatus() === 'exact' ? '#15803d' : getDifferenceStatus() === 'warning' ? '#b45309' : '#b91c1c'
                            }}>
                                {formatCurrency(session.difference)}
                            </div>
                            <div className="sub-detail" style={{
                                color: getDifferenceStatus() === 'exact' ? '#166534' : getDifferenceStatus() === 'warning' ? '#92400e' : '#991b1b'
                            }}>
                                {getDifferenceStatus() === 'exact' ? 'Sin discrepancia' : getDifference() > 0 ? 'Sobrante en caja' : 'Faltante en caja'}
                            </div>
                        </div>

                        {/* NEW: Base Left and Handover */}
                        <div className="closing-card" style={{ background: '#fff7ed', borderColor: '#ffedd5' }}>
                            <div className="label" style={{ color: '#c2410c' }}>Base a Dejar</div>
                            <div className="amount" style={{ color: '#9a3412' }}>
                                {formatCurrency(session.base_left)}
                            </div>
                            <div className="sub-detail" style={{ color: '#7c2d12' }}>Queda en caja para mañana</div>
                        </div>

                        <div className="closing-card" style={{ background: '#f0fdf4', borderColor: '#4ade80', borderWidth: '2px' }}>
                            <div className="label" style={{ color: '#16a34a', fontWeight: '800' }}>🏦 Efectivo a Entregar</div>
                            <div className="amount" style={{ color: '#15803d' }}>
                                {formatCurrency(parseFloat(session.closing_balance_declared || 0) - parseFloat(session.base_left || 0))}
                            </div>
                            <div className="sub-detail" style={{ color: '#14532d', fontWeight: 'bold' }}>Monto neto para depósito</div>
                        </div>
                    </div>

                    {/* Arqueo de Billetes */}
                    {getBreakdown() && (
                        <div className="arqueo-section">
                            <h3>💵 Arqueo de Billetes</h3>
                            <table className="arqueo-table">
                                <thead>
                                    <tr>
                                        <th>Denominación</th>
                                        <th>Cantidad</th>
                                        <th>Subtotal</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {BILL_DENOMINATIONS.map(denom => {
                                        const qty = getBreakdown()[denom.key];
                                        if (!qty || parseFloat(qty) === 0) return null;
                                        const subtotal = denom.isCoins
                                            ? parseFloat(qty)
                                            : parseInt(qty) * denom.value;
                                        return (
                                            <tr key={denom.key}>
                                                <td>{denom.label}</td>
                                                <td>{denom.isCoins ? formatCurrency(qty) : parseInt(qty)}</td>
                                                <td>{formatCurrency(subtotal)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td colSpan={2}>Total Declarado</td>
                                        <td>{formatCurrency(session.closing_balance_declared)}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}

                    {/* Quick Metrics */}
                    {(() => {
                        const metrics = getMetrics();
                        return (
                            <div className="closing-metrics">
                                <div className="metric-item">
                                    <div className="metric-icon" style={{ background: '#dcfce7' }}>📥</div>
                                    <div>
                                        <div className="metric-value">{metrics.incomeCount}</div>
                                        <div className="metric-label">Operaciones de Ingreso</div>
                                    </div>
                                </div>
                                <div className="metric-item">
                                    <div className="metric-icon" style={{ background: '#fee2e2' }}>📤</div>
                                    <div>
                                        <div className="metric-value">{metrics.expenseCount}</div>
                                        <div className="metric-label">Egresos Registrados</div>
                                    </div>
                                </div>
                                <div className="metric-item">
                                    <div className="metric-icon" style={{ background: '#e0e7ff' }}>📊</div>
                                    <div>
                                        <div className="metric-value">{formatCurrency(metrics.avgIncome)}</div>
                                        <div className="metric-label">Promedio por Operación</div>
                                    </div>
                                </div>
                            </div>
                        );
                    })()}

                    {/* Export Actions */}
                    <div className="closing-actions">
                        <button
                            onClick={() => window.open(exportUrl('pdf'), '_blank')}
                            className="px-6 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
                        >
                            📄 Descargar PDF
                        </button>
                        <button
                            onClick={() => window.open(exportUrl('csv'), '_blank')}
                            className="px-6 py-3 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 transition-colors"
                        >
                            📋 Descargar CSV
                        </button>
                        <button
                            onClick={onRefresh}
                            className="px-6 py-3 bg-gray-600 text-white rounded-lg font-semibold hover:bg-gray-700 transition-colors"
                        >
                            🔄 Actualizar
                        </button>
                    </div>
                </div>
            )}

            {/* Quick Actions (Open sessions only) */}
            {session.status === 'open' && (
                <div className="flex gap-3 justify-center flex-wrap">
                    <button
                        onClick={onOpenExpense}
                        className="px-6 py-3 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700 transition-colors"
                    >
                        📤 Registrar Egreso
                    </button>

                    <button
                        onClick={() => window.open(exportUrl('pdf'), '_blank')}
                        className="px-6 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
                    >
                        📄 Descargar PDF
                    </button>

                    <button
                        onClick={onRefresh}
                        className="px-6 py-3 bg-gray-600 text-white rounded-lg font-semibold hover:bg-gray-700 transition-colors"
                    >
                        🔄 Actualizar
                    </button>

                    <button
                        onClick={() => setShowClosingModal(true)}
                        className="px-6 py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors"
                    >
                        🔒 Cerrar Caja
                    </button>
                </div>
            )}

            {/* Blind Closing Modal */}
            {showClosingModal && (
                <BlindClosingModal
                    session={session}
                    onClose={() => setShowClosingModal(false)}
                    onSuccess={onSessionClosed}
                />
            )}
        </div>
    );
};

export default CashBoxOverview;
